import { addDays, parseISO, format, differenceInCalendarDays } from 'date-fns'
import config from 'isomorphic-config'
import pclnConfig from '@pcln/config'
import { HotelsFill, FlightsFill, CarsFill } from '@pcln/brand'
import { Cars, Flights, Hotels } from 'pcln-icons'
import analytics from '@/shared-utils/analytics'
import { ANALYTICS_CATEGORY_HOME } from '@/constants/analytics'
import cleanSlashes from '@/shared-utils/clean-slashes'
import {
  CrossSellContentBlock,
  Booking,
  CarInventoryParams,
  HotelInventoryParams,
  Product,
  ProductIconType
} from './types'

const hosts = pclnConfig.hosts ?? {}
const baseUrl = hosts.baseUrl || '/'
const baseUrlSecure = hosts.baseUrlSecure || '/'
type ServiceConfig = {
  uri: string
  secure: boolean
}
const services: Record<string, ServiceConfig> = pclnConfig.services ?? {}
const itineraryServiceConfig = config.client['itinerary-service']

export const getLinkUri = (serviceName: string, endpoint: string) => {
  const service = services[serviceName]
  if (!service) {
    throw new Error(`service ${serviceName} not configured`)
  }
  const base = service.secure ? baseUrlSecure : baseUrl
  return `${cleanSlashes(base)}/${cleanSlashes(service.uri)}${
    endpoint ? `/${cleanSlashes(endpoint)}` : ''
  }`
}

const PCLN_ITINERARY_SERVICE_URL =
  itineraryServiceConfig.url || getLinkUri('itinerary-service', 'graph')
const PCLN_ITINERARY_SERVICE_TIMEOUT = itineraryServiceConfig.timeout

const serviceOptions = {
  url: PCLN_ITINERARY_SERVICE_URL,
  method: 'POST',
  timeout: PCLN_ITINERARY_SERVICE_TIMEOUT
}

export function buildContent(booking: Booking): CrossSellContentBlock {
  const startDate = parseISO(booking.travelStartDateTime)
  const endDate = parseISO(booking.travelEndDateTime)
  const formattedDate = `${format(startDate, 'EEE, MMM dd')} - ${format(
    endDate,
    'EEE, MMM dd'
  )}`

  const hotelsXSellUrl = (destination: string, start: Date, end: Date) =>
    `/relax/in/${destination}/from/${format(start, 'yyyyMMdd')}/to/${format(
      end,
      'yyyyMMdd'
    )}/rooms/1`

  switch (booking.productId) {
    case Product.Hotel: {
      const checkInDate = parseISO(booking.utcCheckInDateTime)

      return {
        product: 'hotel',
        xSellMessaging: 'Your hotel booking unlocks up to 40% off rental cars',
        xSellTarget: 'Rental Cars',
        date: formattedDate,
        offerToken: booking.offerToken,
        headline: booking.destinationCity,
        subHeadline: `Check-in on ${format(checkInDate, 'EEE, MMM dd')}`,
        xSellUrl: `/drive/search/r/${booking.destinationCity},${
          booking.destinationCountryCode
        }/${booking.destinationCity},${booking.destinationCountryCode}/${format(
          startDate,
          'yyyyMMdd'
        )}-12:00/${format(endDate, 'yyyyMMdd')}-12:00/list`
      }
    }
    case Product.Flight: {
      const destination =
        booking.destinationCity || booking.destinationLocationCode

      const isSameDay = differenceInCalendarDays(endDate, startDate) < 1
      return {
        product: 'flight',
        xSellMessaging:
          'Your flight booking unlocks VIP hotel deals of up to 40% off',
        xSellTarget: 'Hotels',
        offerToken: booking.offerToken,
        date: formattedDate,
        headline: `${booking.originLocationCode} - ${booking.destinationLocationCode}`,
        subHeadline: `Departs ${format(startDate, "EEE, MMM dd 'at' h:mm a")}`,
        xSellUrl: hotelsXSellUrl(
          destination,
          startDate,
          isSameDay ? addDays(endDate, 1) : endDate
        )
      }
    }
    case Product.Car: {
      const checkInDate = parseISO(booking.utcCheckInDateTime)

      return {
        product: 'car',
        xSellMessaging:
          'Your rental car booking unlocks VIP hotel deals of up to 40% off',
        xSellTarget: 'Hotels',
        offerToken: booking.offerToken,
        date: formattedDate,
        headline: `${booking.destinationCity}`,
        subHeadline: `Pickup on ${format(checkInDate, 'EEE, MMM do')}`,
        xSellUrl: hotelsXSellUrl(booking.destinationCity, startDate, endDate)
      }
    }
    // no default
  }
}

export function buildRCInventoryRequest({
  pickupDateTime,
  returnDateTime,
  pickupLocation,
  returnLocation,
  offerToken,
  tokenType
}: CarInventoryParams) {
  return {
    ...serviceOptions,
    data: {
      query: `{
        rcXsell(
          pickupDateTime: "${pickupDateTime}",
          returnDateTime: "${returnDateTime}",
          pickupLocation: "${pickupLocation}",
          returnLocation: "${returnLocation}",
          offerToken: "${offerToken}",
          tokenType: "${tokenType}"
        ) {
          listings: content {
            id
            checkoutUrl
            carExample
            isCarExampleExact
            pricing {
              price
              currencySymbol
              disclaimer
              priceDescription
            }
            partner {
              name
              url
            }
            options
            bannerType
            carImageUrl
            vehicleFeatures {
              bagCapacity
              isAirConditioned
              isUnlimitedMileage
              mileageDistance
              mileageUnit
              peopleCapacity
            }
            rate {
              currencyCode
              currencySymbol
              dailyPrice
              isFreeCancellation
              isPrepay
              savingsPriceTotal
              strikeThroughPrice
              totalPrice
            }
          }
        }
      }`
    }
  }
}

export function buildHotelInventoryRequest({
  cityId,
  cityName,
  checkIn,
  checkOut,
  cguid,
  appc,
  rid,
  productId,
  pageSize = 3,
  dealTypes
}: HotelInventoryParams) {
  return {
    ...serviceOptions,
    data: {
      variables: {
        cityId,
        cityName,
        checkIn,
        checkOut,
        cguid,
        appc,
        rid,
        productId,
        pageSize,
        dealTypes
      },
      query: `{
        integratedListings(
          cityId: ${cityId}
          cityName: "${cityName}"
          checkIn: ${checkIn}
          checkOut: ${checkOut}
          appc: "${appc}"
          cguid: "${cguid}"
          rid: "${rid}"
          productId: ${productId}
          pageSize: ${pageSize}
        ) {
          listings {
            name
            location
            imageUrl
            guestRating
            guestReviewCount
            starRating
            savingPercentage
            originalPrice
            currentPrice
            currency
            currencySymbol
            hotelId
            href
          }
        }
      }`
    }
  }
}

export function buildItineraryDetailsRequest(
  offerToken: string,
  tokenType: string
) {
  return {
    ...serviceOptions,
    data: {
      query: `{
        itineraryDetails(offerToken: "${offerToken}", tokenType: "${tokenType}") {
          hotelXsellCarousel {
            checkIn
            checkOut
            cityId
            cityName
          }
          rcXsellRequestParams {
            pickupDateTime
            returnDateTime
            pickupLocation
            returnLocation
          }
        }
      }`
    }
  }
}

export function trackCrossSellClicks() {
  analytics.fireEvent({
    action: 'Feature Box CTA',
    category: ANALYTICS_CATEGORY_HOME,
    label: 'Cross Sell Choose Button'
  })
}

/**
 * This is needed because of the following scenarios
 *
 * view more button with inventory - example text is "view more rental car deals" - product is singular and lowercase
 * search button with no inventory - example text is "Search Rental Cars" - product is upper case and plural
 */

export const X_SELL_PRODUCTS = {
  'Rental Cars': 'rental car',
  Hotels: 'hotel',
  Flights: 'flight'
} as const

export const trackViewTripClick = () => {
  analytics.fireEvent({
    action: 'Feature Box Link',
    category: ANALYTICS_CATEGORY_HOME,
    label: 'Cross Sell View Trip'
  })
}

export const getProductIconFilled = (product: string): ProductIconType => {
  switch (product) {
    case 'flight':
    case 'Flights':
      return FlightsFill
    case 'car':
    case 'Rental Cars':
      return CarsFill
    default:
      return HotelsFill
  }
}

export const getProductIcon = (product: string): ProductIconType => {
  switch (product) {
    case 'flight':
    case 'Flights':
      return Flights
    case 'car':
    case 'Rental Cars':
      return Cars
    default:
      return Hotels
  }
}
